/**
 * Single source of truth for converting a Shopify product handle to the same
 * `offerId` the `google-merchant-feed` edge function writes into Google Merchant
 * Center. Used for all ecommerce `item_id` values (view_item, add_to_cart,
 * begin_checkout, purchase) so analytics and Ads match the feed exactly.
 *
 * Algorithm — MUST stay byte-identical to
 * `supabase/functions/google-merchant-feed/index.ts` (~line 842-852):
 *   1. Reject handles containing ®, ™ or © (GMC rejects them — these products
 *      are excluded from the feed entirely, so no offerId exists).
 *   2. If handle.length <= 50 → return the handle as-is.
 *   3. Else → `handle.substring(0, 43) + "-" + base36(hash).substring(0, 6)`,
 *      where `hash` is the classic 32-bit djb2-style rolling hash:
 *      `hash = ((hash << 5) - hash + charCode) | 0` over the full handle,
 *      then `Math.abs(hash).toString(36)`.
 *
 * Output length is always ≤ 50 chars, matching Merchant Center's id constraint.
 */

const SPECIAL_CHAR_RE = /[®™©]/;

export function toFeedOfferId(handle: string | null | undefined): string | null {
  if (!handle) return null;
  const trimmed = handle.trim();
  if (trimmed.length === 0) return null;
  if (SPECIAL_CHAR_RE.test(trimmed)) return null;
  if (trimmed.length <= 50) return trimmed;

  let hash = 0;
  for (let i = 0; i < trimmed.length; i++) {
    hash = ((hash << 5) - hash + trimmed.charCodeAt(i)) | 0;
  }
  return trimmed.substring(0, 43) + "-" + Math.abs(hash).toString(36).substring(0, 6);
}

/**
 * Convenience for ecommerce events. Falls back through productHandle →
 * product.node.handle → variantId so a missing handle never breaks the event,
 * but the variantId fallback is logged so we can spot gaps in the feed-parity
 * coverage.
 */
export function cartItemToFeedOfferId(item: {
  productHandle?: string | null;
  product?: { node?: { handle?: string | null } };
  variantId: string;
}): string {
  const handle = item.productHandle ?? item.product?.node?.handle ?? null;
  const offerId = toFeedOfferId(handle);
  if (offerId) return offerId;
  // Last-resort fallback. Should never trigger in normal flows because
  // compactCartItem populates productHandle from product.node.handle.
  return item.variantId;
}
